import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import { apiAuthService } from '@api';
import { apiConnectService, ConnectApiError } from '@api';
import type { ConnectSession } from '@api';
import {
    deriveInitials,
    parseMerchantNameHint,
    parseConnectToken,
    parseOriginHint,
    postCancelMessage,
    postPaymentSuccessMessage,
    postSuccessMessage,
    sanitizeMerchantName
} from '@utils/connect_helpers';
import { useUserContext } from '@hooks/context/useUserContext';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useForm } from '@hooks/helpers/useForm';
import { signInValidation } from '@helpers/validation_objects/signin';
import { useLocales } from '@hooks/helpers/useLocales';
import { defaultSignInData } from '@const/default_form_data';
import { routes } from '@const/routes';
import { ETwoFAMethod, TTwoFALoginResult } from '@xeppt/xeppt-sdk/types';

import Button from '@components/common/button';
import Input from '@components/common/input';
import FormField from '@components/form_field';
import MFALayout from '@layouts/auth/signin/mfa';

import logo from '@svg/dark_header_logo.svg';
import checkoutIllustration from '@svg/illustrations/connect/checkout.svg';
import cartIcon from '@svg/icons/connect/cart.svg';
import shieldSecureIcon from '@svg/icons/connect/shield_secure.svg';
import chevronDownIcon from '@svg/icons/connect/chevron_down.svg';
import clockIcon from '@svg/icons/connect/clock.svg';
import walletIcon from '@svg/icons/connect/wallet.svg';
import closeIcon from '@svg/icons/connect/close.svg';
import chevronLeftIcon from '@svg/icons/connect/chevron_left.svg';
import logoutIcon from '@svg/icons/connect/logout.svg';
import illustrationDone from '@svg/illustrations/connect/done.svg';
import illustrationError from '@svg/illustrations/connect/error.svg';
import illustrationExpired from '@svg/illustrations/connect/expired.svg';
import styles from './styles.module.scss';

// ─── State machine ────────────────────────────────────────────────────────────

type ConnectState =
    | 'signin'
    | 'mfa'
    | 'loading'
    | 'consent'
    | 'greeting'
    | 'success'
    | 'error'
    | 'expired'
    | 'confirm'
    | 'cart'
    | 'charging'
    | 'paid'
    | 'failed';

type FailureCode = 'insufficient_funds' | 'generic';

// ─── Module-level helpers ─────────────────────────────────────────────────────

function formatAmount(cents: number, currency: string): string {
    const cur = currency || 'CAD';
    return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: cur,
        currencyDisplay: 'narrowSymbol'
    }).format(cents / 100);
}

// S3: amount format — "$388.00 CAD" (formatted symbol + space + ISO code)
function formatMoney(cents: number, currency: string): string {
    return `${formatAmount(cents, currency)} ${currency || 'CAD'}`;
}

function formatCountdown(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const ACTIVE_PAYMENT_STATES: ConnectState[] = ['confirm', 'cart', 'charging'];

// Shared with renderFailedScreen's insufficient_funds case so the pre-charge
// guard and the post-charge failure screen show identical copy.
const INSUFFICIENT_FUNDS_MESSAGE = "You don't have enough balance to complete this payment.";

const ConnectLayout = () => {
    const location = useLocation();
    const { user, handleLogout } = useUserContext();
    const { labelLocale, placeholderLocale, requestErrorLocale } = useLocales();

    // ── Query param extraction (untrusted) ──────────────────────────────────
    const token = parseConnectToken(location.search);
    // Validated origin hint — ONLY used for cancel messages, never for the code.
    const originHint = parseOriginHint(location.search);
    const merchantNameHint = parseMerchantNameHint(location.search);

    // ── Authoritative values from the backend ───────────────────────────────
    const [session, setSession] = useState<ConnectSession | null>(null);

    // ── State machine ────────────────────────────────────────────────────────
    const [connectState, setConnectState] = useState<ConnectState>(() => {
        if (!token) return 'error';
        return 'signin';
    });

    const [mfaData, setMfaData] = useState<TTwoFALoginResult>();

    // ── Profile dropdown ────────────────────────────────────────────────────
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // ── Payment-specific state ───────────────────────────────────────────────
    const [failureCode, setFailureCode] = useState<FailureCode>('generic');
    const [chargeResult, setChargeResult] = useState<{
        transaction_id: string;
        amount: number;
        customer_id?: string;
        username?: string;
    } | null>(null);
    const [countdown, setCountdown] = useState<number | null>(null);
    // Prevents the payment success postMessage from firing more than once.
    const successFiredRef = useRef(false);
    // Prevents a second charge from a double-click landing before the state flush.
    const chargingRef = useRef(false);
    const greetingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // When the user presses "Sign in again" from the expired screen, route any
    // subsequent connect-flow error back to the expired screen instead of the
    // generic error, so they always see a "Return to store" action.
    const retryingFromExpiredRef = useRef(false);

    // ── Consent-specific state ───────────────────────────────────────────────
    const [isConsentLoading, setIsConsentLoading] = useState(false);
    // Prevents a second consent from a double-click before the state flush.
    const consentingRef = useRef(false);

    // ── Payment success poster — fires once, deduplicated via ref ────────────
    // Defined before handleCancel so the terminal 'paid' close path can call it.
    const firePaymentSuccess = useCallback(() => {
        if (successFiredRef.current || !chargeResult || !session) return;
        successFiredRef.current = true;
        postPaymentSuccessMessage(
            chargeResult.transaction_id,
            chargeResult.amount,
            session.redirect_origin,
            chargeResult.customer_id,
            chargeResult.username
        );
        window.close();
    }, [chargeResult, session]);

    // ── Cancel / close helper ───────────────────────────────────────────────
    // Prefer the authoritative backend redirect_origin; fall back to validated hint.
    const handleCancel = useCallback(() => {
        if (connectState === 'charging') return;
        // On the terminal 'paid' screen the charge already succeeded; closing or
        // pressing Escape must notify the merchant of SUCCESS, not cancel. The
        // merchant demo store relies on this postMessage (it has no webhook listener).
        if (connectState === 'paid') {
            firePaymentSuccess();
            return;
        }
        if (greetingTimerRef.current) { clearTimeout(greetingTimerRef.current); greetingTimerRef.current = null; }
        const target = session?.redirect_origin ?? originHint;
        postCancelMessage(target);
        window.close();
    }, [connectState, session, originHint, firePaymentSuccess]);

    // ── Escape key → cancel ─────────────────────────────────────────────────
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleCancel]);

    // ── Focus trap: keep Tab inside the modal ───────────────────────────────
    const modalRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const modal = modalRef.current;
        if (!modal) return;
        modal.focus();
        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const focusable = modal.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
            );
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first || !last) return;
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        modal.addEventListener('keydown', handleTab);
        return () => modal.removeEventListener('keydown', handleTab);
    }, []);

    // ── Close profile dropdown on click-outside ─────────────────────────────
    useEffect(() => {
        if (!isDropdownOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    // ── Connect flow: getSession → (payment: greeting→confirm) | (identity: consent → success) ──
    const runConnectFlow = useCallback(async () => {
        if (!token) {
            setConnectState('error');
            return;
        }
        setConnectState('loading');
        try {
            const sessionData = await apiConnectService.getSession(token);
            setSession(sessionData);
            if (typeof sessionData.amount_cents === 'number' && sessionData.amount_cents > 0) {
                // Payment session: show greeting for ~1 s (design spec: 0.8–1.5 s) then confirm.
                setConnectState('greeting');
                greetingTimerRef.current = setTimeout(() => setConnectState('confirm'), 1000);
                return;
            }
            // Identity-only session: branch on scope.
            const scopes = (sessionData.scope ?? '').split(' ').filter(Boolean);
            if (scopes.includes('signin')) {
                // Explicit consent required: show consent screen and await user action.
                setConnectState('consent');
                return;
            }
            // Plain identity (no signin scope) → silent consent, unchanged behavior.
            const { code } = await apiConnectService.consent(token);
            setConnectState('success');
            // Show success for 1 s, then post and close.
            setTimeout(() => {
                postSuccessMessage(code, sessionData.redirect_origin);
                window.close();
            }, 1000);
        } catch {
            // If the user pressed "Sign in again" from the expired screen and the
            // connect session is still expired server-side, route back to the
            // friendly expired screen (with "Return to store") not the generic dead-end.
            const wasRetryingFromExpired = retryingFromExpiredRef.current;
            retryingFromExpiredRef.current = false;
            setConnectState(wasRetryingFromExpired ? 'expired' : 'error');
        }
    }, [token]);

    // ── Kick off the connect flow once the user is authenticated ─────────────
    // Single source of truth: fires when `user` resolves (already-logged-in on
    // mount) or after a successful in-popup login. The `connectState === 'signin'`
    // guard prevents re-entry — runConnectFlow flips state to 'loading' before its
    // first await — while still allowing an explicit retry from the error screen
    // ('error' → 'signin'). A second mount-effect here caused a double getSession/
    // consent race for already-authenticated users; collapsing to one effect fixes it.
    useEffect(() => {
        if (user && connectState === 'signin') {
            void runConnectFlow();
        }
    }, [user, connectState, runConnectFlow]);

    // ── Countdown: ticks every second during active payment states ───────────
    useEffect(() => {
        if (!session?.expires_at) return;
        if (!ACTIVE_PAYMENT_STATES.includes(connectState)) return;
        const expiresAt = new Date(session.expires_at).getTime();
        // Guard a malformed expires_at: NaN would render "NaN:NaN" and never hit
        // the remaining === 0 expiry branch. Treat unparseable as "no countdown".
        if (isNaN(expiresAt)) return;
        const tick = () => {
            const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
            setCountdown(remaining);
            if (remaining === 0) {
                setConnectState('expired');
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [connectState, session?.expires_at]);

    // ── Charge handler ───────────────────────────────────────────────────────
    const handleCharge = useCallback(async () => {
        if (chargingRef.current || !token || !session) return;
        chargingRef.current = true;
        setConnectState('charging');
        try {
            const result = await apiConnectService.charge(token);
            setChargeResult({
                transaction_id: result.transaction_id,
                amount: result.amount,
                customer_id: result.customer_id,
                username: result.username
            });
            setConnectState('paid');
        } catch (err) {
            if (err instanceof ConnectApiError && err.code === 'INSUFFICIENT_FUNDS') {
                setFailureCode('insufficient_funds');
            } else {
                setFailureCode('generic');
            }
            setConnectState('failed');
        } finally {
            chargingRef.current = false;
        }
    }, [token, session]);

    // Auto-fire success message ~1.5 s after reaching the paid screen so the
    // merchant is notified even if the user closes the window without clicking.
    useEffect(() => {
        if (connectState !== 'paid') return;
        const id = setTimeout(firePaymentSuccess, 1500);
        return () => clearTimeout(id);
    }, [connectState, firePaymentSuccess]);

    // ── Consent allow handler ────────────────────────────────────────────────
    const handleConsentAllow = useCallback(async () => {
        if (consentingRef.current || !token || !session) return;
        consentingRef.current = true;
        setIsConsentLoading(true);
        try {
            const { code } = await apiConnectService.consent(token);
            setConnectState('success');
            setTimeout(() => {
                postSuccessMessage(code, session.redirect_origin);
                window.close();
            }, 1000);
        } catch {
            setConnectState('error');
        } finally {
            consentingRef.current = false;
            setIsConsentLoading(false);
        }
    }, [token, session]);

    // ── Sign-in form ─────────────────────────────────────────────────────────
    const {
        control,
        formState: { isValid },
        getValues,
        reset
    } = useForm({
        defaultValues: defaultSignInData,
        resolver: zodResolver(
            signInValidation({
                messages: {
                    email: 'Please enter a valid email',
                    mfa: 'Please enter the verification code'
                },
                isMFA: !!mfaData
            })
        )
    });

    const { handleRequest: handleVerify, isLoading: isVerifyLoading } = useApiMutation({
        method: () =>
            apiAuthService.loginVerify({
                token: mfaData?.token ?? '',
                code: getValues().mfaCode
            }),
        onSuccess: () => {
            void runConnectFlow();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleFallback } = useApiMutation({
        method: () => apiAuthService.fallbackTwoFA(mfaData?.token ?? ''),
        onSuccess: (data) => setMfaData(data),
        onError: requestErrorLocale
    });

    const { handleRequest: handleSubmit, isLoading: isSignInLoading } = useApiMutation({
        method: () => {
            const { email, password } = getValues();
            return apiAuthService.login({ email_or_phone: email, password });
        },
        onSuccess: (data) => {
            if (data && data.method !== ETwoFAMethod.NONE) {
                setMfaData(data);
                setConnectState('mfa');
            } else {
                void runConnectFlow();
            }
        },
        onError: requestErrorLocale
    });

    // ── Resolve merchant display name ────────────────────────────────────────
    // After getSession, use the authoritative name; before that, use the sanitized hint.
    // Sanitize the backend value too (strip tags, cap length) — defense in depth,
    // even though React escapes text nodes.
    const merchantName = session?.merchant_name
        ? sanitizeMerchantName(session.merchant_name)
        : merchantNameHint;

    // ── User display info ────────────────────────────────────────────────────
    const firstName = user?.profile?.first_name ?? '';
    const lastName = user?.profile?.last_name ?? '';
    const userEmail = user?.email ?? '';
    const avatarUrl = user?.profile?.avatar ?? '';

    // ── Payment session convenience values ───────────────────────────────────
    const currency = session?.currency ?? 'CAD';
    const amountCents = session?.amount_cents ?? 0;
    const walletBalanceCents = session?.wallet_balance_cents ?? 0;
    // Pre-charge guard: the backend re-validates at charge time regardless,
    // but showing this upfront avoids a doomed charge attempt.
    const insufficientBalance = walletBalanceCents < amountCents;

    // ─────────────────────────────────────────────────────────────────────────
    // Payment screen renderers
    // ─────────────────────────────────────────────────────────────────────────

    const renderConfirmScreen = () => {
        const isCharging = connectState === 'charging';
        const hasLineItems = (session?.line_items?.length ?? 0) > 0;
        const fullName = `${firstName} ${lastName}`.trim();
        return (
            <div className={styles.payment_confirm} aria-live="polite">
                <div className={styles.top_bar}>
                    {/* S5: avatar opens profile dropdown */}
                    <div ref={dropdownRef} className={styles.profile_dropdown_wrapper}>
                        <button
                            type="button"
                            className={styles.avatar_btn}
                            aria-label="User profile menu"
                            aria-haspopup="menu"
                            aria-expanded={isDropdownOpen}
                            disabled={isCharging}
                            onClick={isCharging ? undefined : () => setIsDropdownOpen((prev) => !prev)}>
                            <div className={styles.avatar_sm} aria-hidden="true">
                                {avatarUrl ? (
                                    <img src={avatarUrl} alt="" className={styles.avatar_sm_img} />
                                ) : (
                                    deriveInitials(firstName, lastName)
                                )}
                            </div>
                            {/* Figma: "Icons/Chevron Arrow Down 18x18" in Profile row. */}
                            <img
                                src={chevronDownIcon}
                                width={18}
                                height={18}
                                alt=""
                                aria-hidden="true"
                                className={styles.avatar_chevron}
                            />
                        </button>
                        {isDropdownOpen && (
                            <div className={styles.profile_dropdown} role="menu">
                                <div className={styles.dropdown_user_info}>
                                    <p className={styles.dropdown_name}>{fullName}</p>
                                    <p className={styles.dropdown_email}>{userEmail}</p>
                                </div>
                                <button
                                    type="button"
                                    className={styles.dropdown_signout}
                                    role="menuitem"
                                    onClick={() => {
                                        if (isCharging) return;
                                        setIsDropdownOpen(false);
                                        handleLogout();
                                        setConnectState('signin');
                                    }}>
                                    {/* Figma: "Icons/Logout 24x24" white stroke, left of the button text. */}
                                    <img src={logoutIcon} width={24} height={24} alt="" aria-hidden="true" />
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                    <img src={logo} alt="XEPPT" className={styles.top_logo} />
                    {countdown !== null && (
                        <div
                            className={styles.countdown_pill}
                            role="timer"
                            aria-live="off"
                            aria-label={`Session expires in ${formatCountdown(countdown)}`}>
                            {/* Figma: "Icons/Time Circle 18x18" in countdown pill. */}
                            <img src={clockIcon} width={18} height={18} alt="" aria-hidden="true" />
                            <span>{formatCountdown(countdown)}</span>
                        </div>
                    )}
                </div>
                {/* S2: static row when no line items; underlined text-button when there are */}
                <div className={styles.order_row}>
                    <span className={styles.order_row_left}>
                        <img src={cartIcon} width={24} height={24} alt="" aria-hidden="true" />
                        {hasLineItems ? (
                            <button
                                type="button"
                                className={styles.order_row_link}
                                onClick={() => setConnectState('cart')}
                                aria-label="View cart details">
                                Your order
                            </button>
                        ) : (
                            <span className={styles.order_row_label}>Your order</span>
                        )}
                    </span>
                    <span className={styles.order_total}>{formatMoney(amountCents, currency)}</span>
                </div>
                <div className={styles.divider} aria-hidden="true" />
                <p className={styles.pay_with_label}>Pay with</p>
                <div className={styles.wallet_row}>
                    <span className={styles.wallet_row_left}>
                        <img src={walletIcon} width={24} height={24} alt="" aria-hidden="true" />
                        <span>XEPPT wallet</span>
                    </span>
                    <span className={styles.wallet_amount}>
                        {formatMoney(Math.max(0, walletBalanceCents), currency)}
                    </span>
                </div>
                {insufficientBalance && (
                    <p className={styles.error_body}>{INSUFFICIENT_FUNDS_MESSAGE}</p>
                )}
                <div className={styles.payment_action}>
                    <Button
                        variant="primary"
                        full
                        isLoading={isCharging}
                        disabled={isCharging || insufficientBalance}
                        onClick={
                            isCharging || insufficientBalance
                                ? undefined
                                : () => { void handleCharge(); }
                        }>
                        Complete payment
                    </Button>
                    <div className={styles.secure_row}>
                        <img src={shieldSecureIcon} width={16} height={16} alt="" aria-hidden="true" />
                        <span>Secure &amp; encrypted by XEPPT</span>
                    </div>
                </div>
                {/* Figma: "Content" frame uses space-between — only the cancel link is
                    pinned to the bottom of the panel, the button/secure block is not. */}
                <div className={styles.confirm_cancel}>
                    <button
                        type="button"
                        className={styles.cancel_link}
                        onClick={handleCancel}
                        aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                        Cancel and return to {merchantName ? merchantName : 'the store'}
                    </button>
                </div>
            </div>
        );
    };

    const renderCartScreen = () => {
        const lineItems = session?.line_items ?? [];
        const subtotal = session?.subtotal_cents ?? 0;
        const shipping = session?.shipping_cents ?? 0;
        const fees = session?.fee_cents ?? 0;
        return (
            <div className={styles.cart_screen} aria-live="polite">
                <button
                    type="button"
                    className={styles.cart_back}
                    onClick={() => setConnectState('confirm')}
                    aria-label="Back to checkout">
                    {/* Figma: "Icons/Chevron Arrow Left 18x18" in cart back button. */}
                    <img src={chevronLeftIcon} width={18} height={18} alt="" aria-hidden="true" />
                    Back to checkout
                </button>
                <p className={styles.cart_heading}>Cart details</p>
                <div className={styles.cart_table_header} aria-hidden="true">
                    <span className={styles.cart_col_item}>Item</span>
                    <span className={styles.cart_col_qty}>Qty</span>
                    <span className={styles.cart_col_price}>Price</span>
                </div>
                {/* Scrollable items region — summary + total stay anchored below */}
                <div className={styles.cart_items_list}>
                    {lineItems.map((item, idx) => (
                        <div key={idx} className={styles.cart_item_row}>
                            <div className={styles.cart_item_info}>
                                <span className={styles.cart_item_name}>{item.name}</span>
                                <span className={styles.cart_item_desc} title={item.description}>
                                    {item.description}
                                </span>
                            </div>
                            <span className={styles.cart_item_qty}>×{item.quantity}</span>
                            <span className={styles.cart_item_price}>
                                {formatAmount(item.unit_price_cents * item.quantity, currency)}
                            </span>
                        </div>
                    ))}
                </div>
                <div className={styles.cart_summary_row}>
                    <span className={styles.cart_summary_label}>Subtotal</span>
                    <span className={styles.cart_summary_value}>{formatAmount(subtotal, currency)}</span>
                </div>
                {shipping > 0 && (
                    <div className={styles.cart_summary_row}>
                        <span className={styles.cart_summary_label}>Shipping</span>
                        <span className={styles.cart_summary_value}>
                            {formatAmount(shipping, currency)}
                        </span>
                    </div>
                )}
                {fees > 0 && (
                    <div className={styles.cart_summary_row}>
                        <span className={styles.cart_summary_label}>Estimated fees</span>
                        <span className={styles.cart_summary_value}>{formatAmount(fees, currency)}</span>
                    </div>
                )}
                <div className={styles.divider} aria-hidden="true" />
                <div className={styles.cart_total_row}>
                    <span>Order total</span>
                    <span>{formatMoney(amountCents, currency)}</span>
                </div>
            </div>
        );
    };

    const renderPaidScreen = () => (
        <div className={styles.paid_panel} aria-live="polite">
            <img src={logo} alt="XEPPT" className={styles.logo} />
            {/* Figma: "Illustration/Done 120x120" renders at 100×100. */}
            <img
                src={illustrationDone}
                width={100}
                height={100}
                alt=""
                aria-hidden="true"
                className={styles.state_illustration}
            />
            <p className={styles.paid_title}>Payment successful</p>
            <p className={styles.paid_body}>Your payment has been completed successfully.</p>
            <Button variant="primary" full onClick={firePaymentSuccess}>
                Return to store
            </Button>
        </div>
    );

    const renderFailedScreen = () => {
        const message =
            failureCode === 'insufficient_funds'
                ? INSUFFICIENT_FUNDS_MESSAGE
                : "We couldn't complete your payment. Please try again later.";
        return (
            <div className={styles.failed_panel} aria-live="assertive">
                <img src={logo} alt="XEPPT" className={styles.logo} />
                {/* Figma: "Illustration/Error 120x120" renders at 100×100. */}
                <img
                    src={illustrationError}
                    width={100}
                    height={100}
                    alt=""
                    aria-hidden="true"
                    className={styles.state_illustration}
                />
                <p className={styles.failed_title}>Payment failed</p>
                <p className={styles.failed_body}>{message}</p>
                <div className={styles.failed_actions}>
                    <Button variant="outlined-primary" size="medium" onClick={handleCancel}>
                        Return to store
                    </Button>
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={() => setConnectState('confirm')}>
                        Try again
                    </Button>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // S1: Session expired — dedicated state (not folded into failed)
    // Design copy: title "Session expired", body per Figma, two-button layout.
    // ─────────────────────────────────────────────────────────────────────────

    const renderExpiredScreen = () => (
        <div className={styles.expired_panel} aria-live="assertive">
            <img src={logo} alt="XEPPT" className={styles.logo} />
            {/* Figma: "Illustration/Payment expired 100x100". */}
            <img
                src={illustrationExpired}
                width={100}
                height={100}
                alt=""
                aria-hidden="true"
                className={styles.state_illustration}
            />
            <p className={styles.failed_title}>Session expired</p>
            <p className={styles.failed_body}>
                Your payment session has expired. Please return to the store to start a new checkout.
            </p>
            <div className={styles.failed_actions}>
                {/* Primary: close the popup so the merchant can initiate a fresh session. */}
                <Button variant="primary" size="medium" onClick={handleCancel}>
                    Return to store
                </Button>
                {/* Secondary: sign in again — on subsequent connect failure, routes to
                    expired (not the generic error) via retryingFromExpiredRef. */}
                <Button
                    variant="outlined-primary"
                    size="medium"
                    onClick={() => {
                        retryingFromExpiredRef.current = true;
                        setConnectState('signin');
                    }}>
                    Sign in again
                </Button>
            </div>
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // S4: Post-sign-in greeting — payment flow only, auto-transitions to confirm
    // Design copy: "Hello, {name}! You've successfully signed in. Getting your
    // XEPPT wallet…" shown for ~1 s before confirm.
    // ─────────────────────────────────────────────────────────────────────────

    const renderGreetingScreen = () => (
        <div className={styles.success_panel} aria-live="polite">
            <img src={logo} alt="XEPPT" className={styles.logo} />
            <div className={styles.avatar} aria-hidden="true">
                {avatarUrl ? (
                    <img src={avatarUrl} alt="" className={styles.avatar_img} />
                ) : (
                    <span className={styles.avatar_initials}>
                        {deriveInitials(firstName, lastName)}
                    </span>
                )}
            </div>
            <p className={styles.success_name}>
                Hello, {firstName} {lastName}!
            </p>
            <p className={styles.success_body}>
                You&apos;ve successfully signed in. Getting your XEPPT wallet&hellip;
            </p>
            <button
                type="button"
                className={styles.cancel_link}
                onClick={handleCancel}
                aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                Cancel and return to{' '}
                {merchantName ? merchantName : 'the store'}
            </button>
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Consent screen — shown for signin-scope identity sessions
    // ─────────────────────────────────────────────────────────────────────────

    const renderConsentScreen = () => (
        <div className={styles.success_panel} aria-live="polite">
            <img src={logo} alt="XEPPT" className={styles.logo} />
            <p className={styles.title}>Connect with {merchantName}</p>
            <p className={styles.subtitle}>
                By continuing, you allow {merchantName} to send money to your XEPPT wallet, now
                and in the future.
            </p>
            <p className={styles.consent_reassurance}>
                {merchantName} will know you have a XEPPT wallet and can send funds to it — shown
                only as a secure reference, never your username, email, or balance.
            </p>
            <Button
                className={styles.submit_btn}
                variant="primary"
                full
                isLoading={isConsentLoading}
                onClick={() => { void handleConsentAllow(); }}>
                Allow
            </Button>
            <button
                type="button"
                className={styles.cancel_link}
                onClick={handleCancel}
                aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                Cancel and return to{' '}
                {merchantName ? merchantName : 'the store'}
            </button>
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Render helpers — identity flow screens (unchanged)
    // ─────────────────────────────────────────────────────────────────────────

    const renderLeftContent = () => {
        switch (connectState) {
            case 'confirm':
            case 'charging':
                return renderConfirmScreen();

            case 'cart':
                return renderCartScreen();

            case 'paid':
                return renderPaidScreen();

            case 'failed':
                return renderFailedScreen();

            case 'expired':
                return renderExpiredScreen();

            case 'greeting':
                return renderGreetingScreen();

            case 'consent':
                return renderConsentScreen();

            case 'success':
                return (
                    <div className={styles.success_panel} aria-live="polite">
                        <div className={styles.avatar} aria-hidden="true">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="" className={styles.avatar_img} />
                            ) : (
                                <span className={styles.avatar_initials}>
                                    {deriveInitials(firstName, lastName)}
                                </span>
                            )}
                        </div>
                        <p className={styles.success_name}>
                            Hello, {firstName} {lastName}!
                        </p>
                        <p className={styles.success_body}>You&apos;ve successfully signed in.</p>
                        <p className={styles.success_body}>Getting your XEPPT wallet&hellip;</p>
                    </div>
                );

            case 'error':
                return (
                    <div className={styles.error_panel} aria-live="assertive">
                        <p className={styles.error_title}>
                            {!token ? 'Invalid request' : 'Session expired'}
                        </p>
                        <p className={styles.error_body}>
                            {!token
                                ? 'No connect token was found in this URL. Please return to the store and try again.'
                                : 'Your session has expired or the connect request is no longer valid.'}
                        </p>
                        <div className={styles.error_actions}>
                            <Button
                                variant="outlined-primary"
                                size="medium"
                                onClick={handleCancel}>
                                Return to store
                            </Button>
                            {token && (
                                <Button
                                    variant="primary"
                                    size="medium"
                                    onClick={() => setConnectState('signin')}>
                                    Sign in again
                                </Button>
                            )}
                        </div>
                    </div>
                );

            case 'loading':
                return (
                    <div className={styles.success_panel} aria-live="polite" aria-busy="true">
                        <p className={styles.subtitle}>Connecting to your XEPPT account&hellip;</p>
                    </div>
                );

            case 'mfa':
                return (
                    <MFALayout
                        method={mfaData?.method ?? ETwoFAMethod.EMAIL}
                        control={control}
                        isLoading={isVerifyLoading}
                        onSubmit={() => handleVerify(undefined)}
                        onFallback={() => {
                            reset({ ...getValues(), mfaCode: '' });
                            setMfaData(undefined);
                            setConnectState('signin');
                            handleFallback(undefined);
                        }}
                    />
                );

            case 'signin':
            default:
                return (
                    <>
                        <img
                            src={logo}
                            alt="XEPPT"
                            className={styles.logo}
                        />
                        <p className={styles.title}>Sign in</p>
                        <p className={styles.subtitle}>
                            to your XEPPT account to continue
                        </p>
                        <div
                            className={styles.form}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && isValid) {
                                    handleSubmit(undefined);
                                }
                            }}>
                            <FormField
                                control={control as any}
                                name="email"
                                renderComponent={(props) => (
                                    <Input
                                        {...props}
                                        full
                                        label={labelLocale('email')}
                                        placeholder={placeholderLocale('enter_email')}
                                        name="email"
                                        autoComplete="email"
                                        autoFocus
                                    />
                                )}
                            />
                            <FormField
                                control={control as any}
                                name="password"
                                renderComponent={(props) => (
                                    <Input
                                        {...props}
                                        full
                                        type="password"
                                        label={labelLocale('password')}
                                        placeholder={placeholderLocale('enter_password')}
                                        name="password"
                                        autoComplete="current-password"
                                    />
                                )}
                            />
                        </div>
                        <Button
                            className={styles.submit_btn}
                            variant="primary"
                            full
                            disabled={!isValid}
                            isLoading={isSignInLoading}
                            onClick={() => handleSubmit(undefined)}>
                            Sign in
                        </Button>
                        <button
                            type="button"
                            className={styles.cancel_link}
                            onClick={handleCancel}
                            aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                            Cancel and return to{' '}
                            {merchantName ? merchantName : 'the store'}
                        </button>
                    </>
                );
        }
    };

    const showFooter = [
        'signin',
        'consent',
        'error',
        'expired',
        'greeting',
        'confirm',
        'charging',
        'cart',
        'paid',
        'failed'
    ].includes(connectState);

    const dialogLabel =
        connectState === 'confirm' || connectState === 'charging'
            ? 'Complete your payment with XEPPT'
            : connectState === 'cart'
              ? 'Cart details'
              : connectState === 'paid'
                ? 'Payment successful'
                : connectState === 'failed'
                  ? 'Payment failed'
                  : connectState === 'expired'
                    ? 'Session expired'
                    : connectState === 'greeting'
                      ? 'Signed in to XEPPT'
                      : connectState === 'consent'
                        ? 'Connect with XEPPT'
                        : 'Sign in with XEPPT';

    return (
        <div className={styles.page}>
            <div
                ref={modalRef}
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-label={dialogLabel}
                tabIndex={-1}>
                {/* Left column */}
                <section className={styles.left}>
                    {renderLeftContent()}
                    {showFooter && (
                        <footer className={styles.footer}>
                            <span className={styles.footer_text}>&copy; 2026 XEPPT</span>
                            <button
                                type="button"
                                className={styles.footer_link}
                                onClick={() =>
                                    window.open(routes.terms_of_service, '_blank', 'noopener')
                                }>
                                Terms of Service
                            </button>
                            <button
                                type="button"
                                className={styles.footer_link}
                                onClick={() =>
                                    window.open(routes.privacy_policy, '_blank', 'noopener')
                                }>
                                Privacy Policy
                            </button>
                        </footer>
                    )}
                </section>

                {/* Right column */}
                <section className={styles.right} aria-hidden="true">
                    <button
                        type="button"
                        className={styles.close_btn}
                        onClick={handleCancel}
                        aria-label="Close"
                        aria-hidden="false">
                        <img src={closeIcon} width={24} height={24} alt="" aria-hidden="true" />
                    </button>
                    <img
                        src={checkoutIllustration}
                        alt=""
                        className={styles.illustration}
                        aria-hidden="true"
                    />
                    <p className={styles.right_heading}>Pay with confidence using XEPPT</p>
                    <p className={styles.right_subtitle}>Fast checkout with built-in security</p>
                </section>
            </div>
        </div>
    );
};

export default ConnectLayout;
